import { useState } from 'react';
import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { Plus, Search, Spline, X } from 'lucide-react';
import { LIBRARY_CATEGORIES, diagramIsEmpty, type Drill, type LibraryCategory, type Plan } from '@hoopify/shared';
import { api, errorMessage } from '../../lib/api';
import { filterLibrary, useLibrary } from '../../lib/library';
import * as P from '../../lib/plan';
import { Alert, Spinner } from '../ui';
import type { DrillSource, Target } from './PlanBuilder';

type Scope = 'mine' | 'shared' | 'hoopify';

interface Row {
  key: string;
  title: string;
  meta: string;
  hasDiagram: boolean;
  source: DrillSource;
}

/** Your drills, drills shared with you, and the Hoopify library: click or drag into the plan. */
export function LibraryPanel({
  plan,
  target,
  onTarget,
  onAdd,
  onDragDrill,
}: {
  plan: Plan;
  target: Target;
  onTarget: (t: Target) => void;
  onAdd: (d: DrillSource) => void;
  onDragDrill?: (d: DrillSource | null) => void;
}) {
  const [scope, setScope] = useState<Scope>('mine');
  const [q, setQ] = useState('');
  const [tag, setTag] = useState<string | null>(null);
  const params = new URLSearchParams({ scope: scope === 'hoopify' ? 'mine' : scope, ...(q.trim() ? { q: q.trim() } : {}), ...(tag ? { tag } : {}) });
  const drills = useQuery({
    queryKey: ['drills', scope, q.trim(), tag],
    queryFn: () => api.get<Drill[]>(`/drills?${params}`),
    placeholderData: (prev) => prev,
    enabled: scope !== 'hoopify',
  });
  const library = useLibrary();

  const rows: Row[] | undefined =
    scope === 'hoopify'
      ? library.data &&
        filterLibrary(library.data, { q, category: (tag as LibraryCategory) ?? null }).map((d) => ({
          key: d.id,
          title: d.title,
          meta: `${d.minutes} min, ${d.category}`,
          hasDiagram: !!d.diagram,
          source: { title: d.title, make: () => P.itemFromLibrary(d) },
        }))
      : drills.data?.map((d) => ({
          key: d.id,
          title: d.title,
          meta: `${d.minutes} min${d.tags.length ? `, ${d.tags.slice(0, 3).join(', ')}` : ''}${!d.mine ? `, from ${d.owner.name}` : ''}`,
          hasDiagram: !diagramIsEmpty(d.diagram),
          source: { title: d.title, make: () => P.itemFromDrill(d) },
        }));
  const loading = scope === 'hoopify' ? library.isLoading : drills.isLoading;
  const error = scope === 'hoopify' ? library.error : drills.error;
  const chips = scope === 'hoopify' ? [...LIBRARY_CATEGORIES] : [...new Set((drills.data ?? []).flatMap((d) => d.tags))].slice(0, 12);

  const alongsideItem =
    target.kind === 'alongside' ? plan.sections.flatMap((s) => s.blocks).find((b) => b.id === target.blockId)?.items[0]?.title : null;

  return (
    <div className="flex max-h-[calc(100dvh-3rem)] flex-col">
      <div className="space-y-3 pb-3">
        <div className="flex items-baseline justify-between">
          <h2 className="text-[15px] font-semibold">Drills</h2>
          <Link to="/drills" className="text-[13px] text-action hover:underline">
            Manage drills
          </Link>
        </div>
        {target.kind === 'alongside' ? (
          <div className="flex items-center gap-2 rounded-md bg-action-soft px-2.5 py-2 text-[13px]">
            <span className="min-w-0 flex-1">
              Adding at the same time as <strong className="font-semibold">{alongsideItem}</strong>
            </span>
            <button className="text-muted hover:text-ink" aria-label="Add to a part instead" onClick={() => onTarget({ kind: 'section', sectionId: plan.sections[0]?.id ?? '' })}>
              <X className="size-4" />
            </button>
          </div>
        ) : (
          <label className="flex items-center gap-2 text-[13px]">
            <span className="text-muted">Add to</span>
            <select className="field-input flex-1 py-1.5 text-[14px]" value={target.sectionId} onChange={(e) => onTarget({ kind: 'section', sectionId: e.target.value })}>
              {plan.sections.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.title || 'Untitled part'}
                </option>
              ))}
            </select>
          </label>
        )}
        <div className="grid grid-cols-3 gap-1 rounded-md bg-sunken p-1 text-[13px]" role="tablist">
          {(['mine', 'shared', 'hoopify'] as const).map((s) => (
            <button
              key={s}
              role="tab"
              aria-selected={scope === s}
              onClick={() => {
                setScope(s);
                setTag(null);
              }}
              className={`rounded py-1 font-medium ${scope === s ? 'bg-surface text-ink shadow-sm' : 'text-muted hover:text-ink'}`}
            >
              {s === 'mine' ? 'Mine' : s === 'shared' ? 'Shared' : 'Hoopify'}
            </button>
          ))}
        </div>
        <div className="relative">
          <Search className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-faint" aria-hidden />
          <input className="field-input pl-8" placeholder="Search drills" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search drills" />
        </div>
        {chips.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {chips.map((t) => (
              <button
                key={t}
                onClick={() => setTag(tag === t ? null : t)}
                aria-pressed={tag === t}
                className={`rounded-full border px-2 py-0.5 text-[12px] ${tag === t ? 'border-action bg-action text-action-ink' : 'border-line-strong text-muted hover:text-ink'}`}
              >
                {t}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="-mx-1 min-h-0 flex-1 overflow-y-auto px-1 pb-2">
        {loading ? (
          <div className="flex items-center gap-2 py-6 text-[14px] text-muted">
            <Spinner /> Loading drills…
          </div>
        ) : error ? (
          <Alert>{errorMessage(error)}</Alert>
        ) : !rows?.length ? (
          <p className="py-4 text-[14px] text-muted">
            {q || tag
              ? 'No drills match.'
              : scope === 'mine'
                ? 'Your library is empty. Try the Hoopify tab, add drills on the Drills page, or type quick drills straight into the plan.'
                : 'No coaches on your teams have shared drills yet.'}
          </p>
        ) : (
          <ul className="space-y-1.5">
            {rows.map((r) => (
              <li
                key={r.key}
                draggable={!!onDragDrill}
                onDragStart={(e) => {
                  e.dataTransfer.effectAllowed = 'copy';
                  e.dataTransfer.setData('text/plain', r.title);
                  onDragDrill?.(r.source);
                }}
                onDragEnd={() => onDragDrill?.(null)}
                className={`flex items-start gap-2 rounded-lg border border-line bg-surface px-3 py-2 ${onDragDrill ? 'cursor-grab active:cursor-grabbing' : ''}`}
              >
                <div className="min-w-0 flex-1">
                  <p className="text-[14px] leading-snug font-medium">{r.title}</p>
                  <p className="tnum mt-0.5 flex items-center gap-1 text-[12px] text-muted">
                    {r.hasDiagram && <Spline className="size-3 shrink-0" aria-label="Has a diagram" />}
                    {r.meta}
                  </p>
                </div>
                <button className="rounded-md p-1.5 text-action hover:bg-action-soft" onClick={() => onAdd(r.source)} aria-label={`Add ${r.title} to the plan`} title="Add to plan">
                  <Plus className="size-4" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
